import React, { useContext, useEffect, useState } from 'react';
import { Form, FormikProps, withFormik } from 'formik';
import classNames from 'classnames';
import { addToCartHandler, updateMiniCart } from '../../../../helpers/addToCartHandler';
import { MessagePropertiesContext } from '../../../../contexts/messageProperties';
import { formFieldsValidation } from '../../../../helpers/formFieldsValidation';
import { formatNumber } from '../../../../helpers/formatPrice';
import AddToCartButton from './addToCartButton';
import QuickSelect from './quickSelect';
import Input from '../../../../components/input';

export const INPUT_NAME = 'giftCardValue';

const {
  quickSelectOptions,
  min = 0,
  max = 0,
  validationRules = {
    messages: {},
    rules: {}
  }
} = window.globalProductDetails.giftCard || {};
const MIN_FORMATTED = formatNumber(min);
const MAX_FORMATTED = formatNumber(max);
const STEP = 1;
const QUANTITY = 1;
const PRODUCT_CODE = 'EGC';
const VALIDATION_MESSAGE = `Choose any value in whole pounds between ${MIN_FORMATTED} and ${MAX_FORMATTED}`;

interface FormData {
  [INPUT_NAME]: string;
}

const initialValues: FormData = {
  [INPUT_NAME]: ''
};

function BuyGiftCardForm({
  values,
  validateForm,
  setFieldValue,
  resetForm
}: FormikProps<FormData>): React.ReactElement {
  const [formattedMessage] = useContext(MessagePropertiesContext);
  const [buttonIsLoading, setButtonIsLoading] = useState(false);
  const [buttonIsDisabled, setButtonIsDisabled] = useState(true);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const normalizedValue = e.target.value.replace(/\D/g, '');
    setFieldValue(INPUT_NAME, normalizedValue);
  };

  const handleSubmit = (): void => {
    const formElement = document.querySelector('.add_to_cart_form') as HTMLFormElement;
    if (formElement) {
      setButtonIsLoading(true);
      addToCartHandler(formElement)
        .then(updateMiniCart)
        .catch(() => {})
        .finally(() => {
          resetForm();
          setButtonIsLoading(false);
        });
    }
  };

  useEffect(() => {
    validateForm().then((errors) => {
      setButtonIsDisabled(Object.keys(errors).length > 0);
    });
  }, [values]);

  return (
    <Form id="giftCardForm" className={classNames('add_to_cart_form', 'giftcard-form')}>
      {quickSelectOptions && (
        <QuickSelect
          handleChange={handleChange}
          selectedAmount={values[INPUT_NAME]}
          quickSelectOptions={quickSelectOptions}
        />
      )}

      <label htmlFor="giftCardValue" className="giftcard-form__input-label">
        {formattedMessage({ id: 'product.giftcard.input.label', fallback: 'Or enter a custom amount' })}
      </label>

      <Input
        id={INPUT_NAME}
        name={INPUT_NAME}
        type="number"
        autoComplete="off"
        min={min}
        max={max}
        step={STEP}
        className="giftcard-form__input"
        label={formattedMessage({ id: 'product.giftcard.input.heading', fallback: 'Enter a value' })}
        onChange={handleChange}
        digitsOnly
        showCurrency
      />

      <span className="giftcard-form__input-message">
        {formattedMessage({
          id: 'product.giftcard.available.values',
          fallback: VALIDATION_MESSAGE,
          values: { 0: MIN_FORMATTED, 1: MAX_FORMATTED }
        })}
      </span>

      <input type="hidden" id="qty" name="qty" value={QUANTITY} />
      <input type="hidden" name="productCodePost" value={PRODUCT_CODE} />

      <AddToCartButton handleSubmit={handleSubmit} isDisabled={buttonIsDisabled} isLoading={buttonIsLoading} />
    </Form>
  );
}

export default withFormik({
  mapPropsToValues: (): FormData => initialValues,
  validate: (values: FormData) => formFieldsValidation(values, validationRules),
  displayName: 'BuyGiftCardFormik',
  handleSubmit: () => {}
})(BuyGiftCardForm);
